import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
    Activity, Server, Database, HardDrive, Cpu, FolderOpen, Plug, Mail, Bot,
    CreditCard, Zap, Bell, ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
    RefreshCw, Download, PlayCircle, Clock
} from "lucide-react";

function Badge({ tone = "ok", children }) {
    const tones = {
        ok: "bg-emerald-100 text-emerald-800 border-emerald-300",
        warn: "bg-amber-100 text-amber-800 border-amber-300",
        crit: "bg-rose-100 text-rose-800 border-rose-300",
        neutral: "bg-muted text-muted-foreground border-border",
    };
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-mono uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
}

function StatusDot({ ok }) {
    if (ok === true) return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    if (ok === false) return <XCircle className="h-4 w-4 text-rose-600" />;
    return <AlertTriangle className="h-4 w-4 text-amber-600" />;
}

function Card({ icon: Icon, title, children, tone = "neutral" }) {
    const borderTones = {
        ok: "border-emerald-200",
        warn: "border-amber-300",
        crit: "border-rose-300",
        neutral: "border-border",
    };
    return (
        <div className={`card-soft p-4 border-2 ${borderTones[tone]}`}>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/50">
                {Icon && <Icon className="h-4 w-4 text-primary" />}
                <h3 className="font-heading text-sm uppercase tracking-widest">{title}</h3>
            </div>
            <div className="space-y-2 text-sm">{children}</div>
        </div>
    );
}

function Row({ label, value, tone }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-xs uppercase font-mono tracking-wide">{label}</span>
            <span className="font-medium text-sm text-right">
                {tone ? <Badge tone={tone}>{value}</Badge> : value}
            </span>
        </div>
    );
}

export default function AdminHealth() {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [lastChecked, setLastChecked] = React.useState(null);
    const [testResult, setTestResult] = React.useState(null);
    const [testing, setTesting] = React.useState(false);

    const load = React.useCallback(async () => {
        try {
            const r = await api.get("/admin/health");
            setData(r.data);
            setLastChecked(new Date());
        } catch (e) {
            toast.error("Nu am putut încărca starea sistemului: " + (e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const runTests = async () => {
        setTesting(true);
        try {
            const r = await api.get("/admin/health/test");
            setTestResult(r.data);
            if (r.data.ok) toast.success("Toate endpoint-urile răspund OK");
            else toast.error("Unele endpoint-uri au eșuat");
        } catch (e) {
            toast.error("Eroare la rulare teste");
        } finally {
            setTesting(false);
        }
    };

    const [smtpResult, setSmtpResult] = React.useState(null);
    const [smtpTesting, setSmtpTesting] = React.useState(false);
    const [smtpTo, setSmtpTo] = React.useState("");
    const runSmtpTest = async () => {
        setSmtpTesting(true); setSmtpResult(null);
        try {
            const r = await api.post("/admin/health/smtp-test", { to: smtpTo || undefined });
            setSmtpResult(r.data);
            if (r.data.ok) toast.success(smtpTo ? `Email test trimis la ${smtpTo}` : "Conexiune SMTP OK");
            else {
                const failed = (r.data.steps || []).find(s => !s.ok);
                toast.error(`SMTP eșec la pasul: ${failed?.step || "necunoscut"}`);
            }
            // re-incarca overview ca să sincronizezi card-ul "Integrări"
            load();
        } catch (e) {
            toast.error("Eroare: " + (e.response?.data?.detail || e.message));
        } finally {
            setSmtpTesting(false);
        }
    };

    const exportJson = () => {
        const blob = new Blob([JSON.stringify({ health: data, tests: testResult }, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `health-${new Date().toISOString().slice(0, 19)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return <div className="flex items-center justify-center py-20 text-muted-foreground"><Activity className="h-6 w-6 animate-pulse mr-2" />Se încarcă starea sistemului...</div>;
    }
    if (!data) {
        return <div className="card-soft p-6"><p className="text-muted-foreground">Nu s-au putut obține datele.</p><button onClick={load} className="btn-primary mt-3">Reîncarcă</button></div>;
    }

    const sys = data.system || {};
    const up = data.uploads || {};
    const integ = data.integrations || {};
    const bg = data.background || {};
    const nl = data.newsletter || {};
    const err = data.errors_24h || {};
    const sec = data.security || {};

    const statusTone = data.status === "ok" ? "ok" : (data.status === "warning" ? "warn" : "crit");

    const diskTone = sys.disk_percent > 90 ? "crit" : (sys.disk_percent > 80 ? "warn" : "ok");
    const ramTone = sys.ram_percent > 90 ? "crit" : (sys.ram_percent > 80 ? "warn" : "ok");
    const uploadsTone = !up.exists ? "crit" : (up.writable ? "ok" : "warn");
    const sslTone = sec.ssl_days_left == null ? "neutral" : (sec.ssl_days_left < 14 ? "crit" : (sec.ssl_days_left < 30 ? "warn" : "ok"));

    return (
        <div data-testid="admin-health">
            {/* HEADER */}
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl flex items-center gap-3">
                        <Activity className="h-7 w-7 text-primary" />
                        Health & Diagnostic
                    </h1>
                    <div className="text-xs text-muted-foreground mt-1 font-mono flex items-center gap-2">
                        <Clock className="h-3 w-3" /> Ultima verificare: {lastChecked ? lastChecked.toLocaleTimeString("ro-RO") : "—"}
                        <span className="mx-2">·</span>
                        Status general:
                        <Badge tone={statusTone}>{data.status?.toUpperCase()}</Badge>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="btn-outline flex items-center gap-2" data-testid="health-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
                    <button onClick={runTests} disabled={testing} className="btn-primary flex items-center gap-2" data-testid="health-run-tests"><PlayCircle className="h-4 w-4" /> {testing ? "Testare..." : "Run full test"}</button>
                    <button onClick={exportJson} className="btn-outline flex items-center gap-2" data-testid="health-export"><Download className="h-4 w-4" /> Export JSON</button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {/* SYSTEM */}
                <Card icon={Server} title="System" tone={sys.mongo_ok ? "ok" : "crit"}>
                    <Row label="Backend" value={<span className="flex items-center gap-1"><StatusDot ok={true} /> 200 OK</span>} />
                    <Row label="Uptime" value={sys.backend_uptime_human || "—"} />
                    <Row label="MongoDB" value={<span className="flex items-center gap-1"><StatusDot ok={sys.mongo_ok} /> {sys.mongo_ok ? sys.mongo_version : (sys.mongo_error || "error")}</span>} />
                    <Row label="Hostname" value={<span className="font-mono text-xs">{sys.hostname}</span>} />
                    <Row label="CPU" value={`${sys.cpu_percent ?? 0}%`} />
                </Card>

                {/* RESOURCES */}
                <Card icon={HardDrive} title="Resurse" tone={diskTone === "crit" || ramTone === "crit" ? "crit" : (diskTone === "warn" || ramTone === "warn" ? "warn" : "ok")}>
                    <div>
                        <div className="flex justify-between text-xs mb-1"><span className="font-mono uppercase text-muted-foreground">Disk</span><span>{sys.disk_used} / {sys.disk_total} <Badge tone={diskTone}>{sys.disk_percent}%</Badge></span></div>
                        <div className="h-2 bg-muted rounded overflow-hidden"><div className={`h-full ${diskTone === "crit" ? "bg-rose-500" : diskTone === "warn" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${sys.disk_percent || 0}%` }} /></div>
                        <div className="text-[10px] text-muted-foreground mt-1">Liber: {sys.disk_free}</div>
                    </div>
                    <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1"><span className="font-mono uppercase text-muted-foreground">RAM</span><span>{sys.ram_used} / {sys.ram_total} <Badge tone={ramTone}>{sys.ram_percent}%</Badge></span></div>
                        <div className="h-2 bg-muted rounded overflow-hidden"><div className={`h-full ${ramTone === "crit" ? "bg-rose-500" : ramTone === "warn" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${sys.ram_percent || 0}%` }} /></div>
                    </div>
                </Card>

                {/* UPLOADS */}
                <Card icon={FolderOpen} title="Uploads" tone={uploadsTone}>
                    <Row label="Path" value={<span className="font-mono text-[11px] break-all">{up.path}</span>} />
                    <Row label="Există" value={<StatusDot ok={up.exists} />} />
                    <Row label="Scriere OK" value={<StatusDot ok={up.writable} />} />
                    {up.owner && <Row label="Owner" value={<span className="font-mono text-xs">{up.owner}:{up.group}</span>} />}
                    <Row label="Fișiere" value={up.file_count ?? 0} />
                    <Row label="Mărime totală" value={up.total_size_human || "0 B"} />
                    {up.latest_file && <Row label="Ultimul upload" value={<span className="font-mono text-[11px]">{up.latest_file} ({up.latest_ago_minutes}m ago)</span>} />}
                </Card>

                {/* INTEGRATIONS */}
                <Card icon={Plug} title="Integrări">
                    <Row label={<span className="flex items-center gap-1"><Mail className="h-3 w-3" /> SMTP</span>} value={<StatusDot ok={integ.smtp_configured} />} />
                    {integ.smtp_host && <div className="text-[10px] text-muted-foreground font-mono -mt-1">{integ.smtp_from || integ.smtp_host}</div>}
                    <Row label={<span className="flex items-center gap-1"><CreditCard className="h-3 w-3" /> Stripe</span>} value={<StatusDot ok={integ.stripe_configured} />} />
                    <Row label={<span className="flex items-center gap-1"><Zap className="h-3 w-3" /> Cheie LLM universală</span>} value={<StatusDot ok={integ.emergent_llm_key_present} />} />
                    <Row label={<span className="flex items-center gap-1"><Bot className="h-3 w-3" /> AI Chat</span>} value={<span className="flex items-center gap-1"><StatusDot ok={integ.ai_chat_enabled} /> {integ.ai_chat_enabled ? integ.ai_chat_provider : "off"}</span>} />
                    {integ.ai_chat_enabled && <div className="text-[10px] text-muted-foreground font-mono -mt-1">model: {integ.ai_chat_model}</div>}
                </Card>

                {/* BACKGROUND */}
                <Card icon={Bell} title="Background tasks">
                    <Row label="Coș abandonat" value={<StatusDot ok={bg.abandoned_cart_enabled} />} />
                    {bg.abandoned_cart_enabled && <>
                        <Row label="Ore declanșare" value={`${bg.abandoned_cart_hours}h`} />
                        <Row label="Scan interval" value={`${bg.abandoned_cart_interval_minutes} min`} />
                        {bg.abandoned_cart_last_run && <Row label="Ultimul scan" value={<span className="font-mono text-[11px]">{new Date(bg.abandoned_cart_last_run).toLocaleString("ro-RO")}</span>} />}
                    </>}
                </Card>

                {/* NEWSLETTER */}
                <Card icon={Mail} title="Newsletter">
                    <Row label="Abonați activi" value={<span className="font-medium">{(nl.active_subscribers || 0).toLocaleString("ro-RO")}</span>} />
                    <Row label="Dezabonați" value={(nl.unsubscribed || 0).toLocaleString("ro-RO")} />
                    {nl.last_campaign_sent_at && <Row label="Ultima campanie" value={<span className="font-mono text-[11px]">{new Date(nl.last_campaign_sent_at).toLocaleString("ro-RO")}</span>} />}
                    {nl.last_campaign_count != null && <Row label="Trimise (ultima)" value={nl.last_campaign_count} />}
                </Card>

                {/* ERRORS */}
                <Card icon={AlertTriangle} title="Erori 24h" tone={(err.count_5xx || 0) > 0 ? "crit" : ((err.count_4xx || 0) > 50 ? "warn" : "ok")}>
                    <Row label="4xx" value={err.count_4xx ?? 0} />
                    <Row label="5xx" value={err.count_5xx ?? 0} tone={(err.count_5xx || 0) > 0 ? "crit" : "ok"} />
                    {err.note && <div className="text-[10px] text-muted-foreground italic">{err.note}</div>}
                </Card>

                {/* SECURITY */}
                <Card icon={ShieldCheck} title="Security / SSL" tone={sslTone === "neutral" ? "neutral" : sslTone}>
                    <Row label="HTTPS" value={<StatusDot ok={sec.https_enabled} />} />
                    {sec.ssl_host && <Row label="Host SSL" value={<span className="font-mono text-[11px]">{sec.ssl_host}</span>} />}
                    {sec.ssl_days_left != null
                        ? <Row label="SSL expiră în" value={<Badge tone={sslTone}>{sec.ssl_days_left} zile</Badge>} />
                        : <div className="text-[11px] text-muted-foreground">SSL check nu e disponibil din container preview — verificare reală se face pe producție.</div>}
                </Card>
            </div>

            {/* SMTP DIAGNOSTIC */}
            <div className="mt-6 card-soft p-4 border-2 border-border">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap pb-2 border-b border-border/50">
                    <div>
                        <h3 className="font-heading text-sm uppercase tracking-widest flex items-center gap-2">
                            <Mail className="h-4 w-4 text-primary" /> Diagnostic SMTP
                            {smtpResult && <Badge tone={smtpResult.ok ? "ok" : "crit"}>{smtpResult.ok ? "OK" : "EȘUEAZĂ"}</Badge>}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">Verifică pas-cu-pas: config → DNS → TCP → STARTTLS → AUTH → trimitere email.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="email" placeholder="email destinatar (opțional)" value={smtpTo} onChange={(e) => setSmtpTo(e.target.value)}
                               className="input-soft text-xs w-64" data-testid="smtp-test-to" />
                        <button onClick={runSmtpTest} disabled={smtpTesting} className="btn-primary flex items-center gap-2 text-sm" data-testid="smtp-test-btn">
                            <PlayCircle className="h-4 w-4" /> {smtpTesting ? "Testare..." : "Testează SMTP"}
                        </button>
                    </div>
                </div>
                {smtpResult ? (
                    <>
                        {smtpResult.summary && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3 text-xs font-mono">
                                <div className="bg-muted px-3 py-2 rounded"><span className="text-muted-foreground uppercase text-[10px]">host</span><div>{smtpResult.summary.host || "—"}</div></div>
                                <div className="bg-muted px-3 py-2 rounded"><span className="text-muted-foreground uppercase text-[10px]">port</span><div>{smtpResult.summary.port}</div></div>
                                <div className="bg-muted px-3 py-2 rounded"><span className="text-muted-foreground uppercase text-[10px]">user</span><div className="truncate">{smtpResult.summary.user || "—"}</div></div>
                                <div className="bg-muted px-3 py-2 rounded"><span className="text-muted-foreground uppercase text-[10px]">from</span><div className="truncate">{smtpResult.summary.from || "—"}</div></div>
                            </div>
                        )}
                        <div className="space-y-1.5">
                            {(smtpResult.steps || []).map((s, i) => (
                                <div key={i} className={`flex items-start gap-3 p-2 rounded border ${s.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                                    <StatusDot ok={s.ok} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono text-xs uppercase tracking-wide font-medium">{s.step}</div>
                                        {s.detail && <div className="text-xs text-muted-foreground">{s.detail}</div>}
                                        {s.error && <div className="text-xs text-rose-700 font-mono break-all whitespace-pre-wrap">⚠ {s.error}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="text-xs text-muted-foreground italic py-2">
                        Apasă „Testează SMTP" pentru a verifica conexiunea. Lasă câmpul email gol pentru test fără trimitere, sau pune un email pentru a primi efectiv un mesaj de probă.
                    </div>
                )}
            </div>

            {/* TEST RESULT */}
            {testResult && (
                <div className="mt-6 card-soft p-4 border-2 border-border">
                    <h3 className="font-heading text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                        <Activity className="h-4 w-4 text-primary" /> Endpoint tests
                        <Badge tone={testResult.ok ? "ok" : "crit"}>{testResult.ok ? "ALL PASSED" : "FAILURES"}</Badge>
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-xs uppercase">
                                <tr><th className="text-left px-2 py-1">Metodă</th><th className="text-left px-2 py-1">Path</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">Timp</th><th></th></tr>
                            </thead>
                            <tbody>
                                {testResult.results.map((r, i) => (
                                    <tr key={i} className="border-t border-border">
                                        <td className="px-2 py-1 font-mono text-xs">{r.method}</td>
                                        <td className="px-2 py-1 font-mono text-xs">{r.path}</td>
                                        <td className="px-2 py-1"><Badge tone={r.ok ? "ok" : "crit"}>{r.status}</Badge></td>
                                        <td className="px-2 py-1 text-xs">{r.ms} ms</td>
                                        <td className="px-2 py-1 text-xs text-muted-foreground">{r.error || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
